<template>
  <v-container
    id="data-tables"
    tag="section"
    fluid
  >
    <base-material-card
      color="indigo"
      icon="mdi-devices"
      inline
      :class="['elevation-4', $vuetify.breakpoint.xs ? 'px-3 py-3' : 'px-5 py-4']"
    >
      <template v-slot:after-heading>
        <div :class="$vuetify.breakpoint.xs ? 'text-h4' : 'display-2'" class="font-weight-light">
          Portals List
        </div>
      </template>
      <filter-component class="mb-3" @handleSearchChild="handleSearch" />

      <v-divider class="mt-2 mb-4" />

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="indigo"
          size="64"
          width="6"
        />
        <div class="mt-4 text-h6 grey--text font-weight-light">
          Loading portals...
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="portals.length === 0" class="text-center py-12">
        <v-icon size="96" color="grey lighten-2" class="mb-4">mdi-devices-off</v-icon>
        <div class="text-h6 grey--text font-weight-light mb-2">
          No portals available
        </div>
        <div class="text-body-2 grey--text mb-4">
          Portals will appear here once added
        </div>
      </div>

      <!-- Data Table -->
      <v-data-table
        v-else
        :headers="headers"
        :items="portals"
        :server-items-length="pagination.total"
        :loading="loading"
        :expanded.sync="expanded"
        single-expand
        show-expand
        hide-default-footer
        class="modern-table"
        :item-class="() => 'table-row'"
        item-key="id"
        :mobile-breakpoint="0"
      >
        <template v-slot:item.status="{item}">
          <v-icon
            :color="item.status =='online'? 'success': 'red'"
          >
            {{ item.status == 'online' ? 'mdi-account-check': 'mdi-account-off' }}
          </v-icon>
          <span
            :class="[item.status == 'online' ?'success--text':'red--text']"
            icon="mdi-eye"
          >{{ item.status }}</span>
        </template>
        <template v-slot:item.data-table-expand="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                icon
                v-bind="attrs"
                class="expand-btn"
                @click.stop="toggleExpand(item)"
                v-on="on"
              >
                <v-icon small color="indigo">
                  {{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isExpanded(item) ? 'Collapse' : 'Expand' }} Details</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                v-if="item.type == 'device'"
                v-bind="attrs"
                icon
                small
                color="indigo"
                class="mr-2 action-btn"
                @click.stop="portalConfig(item)"
                v-on="on"
              >
                <v-icon small>
                  mdi-cog-outline
                </v-icon>
              </v-btn>
            </template>
            <span>Configuration</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                v-if="item.type == 'device'"
                v-bind="attrs"
                icon
                small
                color="indigo"
                class="mr-2 action-btn"
                @click="callRestart(item)"
                v-on="on"
              >
                <v-icon small>
                  mdi-restart
                </v-icon>
              </v-btn>
            </template>
            <span>Restart</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                v-if="item.type == 'device'"
                v-bind="attrs"
                icon
                small
                color="indigo"
                class="mr-2 action-btn"
                @click="getDeviceInfo(item)"
                v-on="on"
              >
                <v-icon small>
                  mdi-wifi
                </v-icon>
              </v-btn>
            </template>
            <span>Network Info</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                icon
                small
                color="error"
                class="action-btn"
                @click="callRemove(item)"
                v-on="on"
              >
                <v-icon small>
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            <span>Remove</span>
          </v-tooltip>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <div class="portal-expanded">
              <div class="portal-expanded__header">
                <div class="portal-expanded__title">
                  <v-icon small color="indigo" class="mr-2">mdi-information-outline</v-icon>
                  Portal details
                </div>
                <!-- <div class="portal-expanded__meta">
                  <v-chip x-small outlined color="indigo" class="mr-2">
                    wallet amount : {{ item.venue_wallet_amount ? item.venue_wallet_amount : '0' }}
                  </v-chip>
                </div> -->
              </div>

              <div class="portal-expanded__grid">
                <div class="portal-expanded__section">
                  <div class="portal-expanded__section-title">
                    <v-icon x-small color="indigo" class="mr-1">mdi-domain</v-icon>
                    Venue
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Name</div>
                    <div class="portal-expanded__v">{{ item.venue_name }}</div>
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Country</div>
                    <div class="portal-expanded__v">{{ item.venue_country }}</div>
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Telephone</div>
                    <div class="portal-expanded__v">{{ item.venue_telephone }}</div>
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Address</div>
                    <div class="portal-expanded__v">{{ item.venue_address }}</div>
                  </div>
                </div>

                <div class="portal-expanded__section">
                  <div class="portal-expanded__section-title">
                    <v-icon x-small color="indigo" class="mr-1">mdi-shield-account</v-icon>
                    Admin / Access
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Admin email</div>
                    <div class="portal-expanded__v">{{ item.admin_email }}</div>
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Portal host</div>
                    <div class="portal-expanded__v portal-expanded__mono">{{ item.admin_api_host }}</div>
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Portal password</div>
                    <div class="portal-expanded__v portal-expanded__mono">{{ item.admin_api_password }}</div>
                  </div>
                  <div class="portal-expanded__kv">
                    <div class="portal-expanded__k">Last active</div>
                    <div class="portal-expanded__v">{{ item.portal_last_active }}</div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </template>
      </v-data-table>
      <div class="text-center pt-2">
        <v-pagination
          v-model="pagination.current"
          :length="pagination.totalPages"
          total-visible="7"
          color="indigo"
          @input="handlePageChange"
        />
      </div>
    </base-material-card>

    <v-dialog
      v-model="configDialog"
      max-width="560"
    >
      <v-card class="portal-config-dialog">
        <v-card-title class="portal-config-dialog__title">
          <div class="d-flex align-center">
            <!-- <v-btn
            icon
            aria-label="Close"
            class="portal-config-dialog__close"
            @click="configDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn> -->
            <v-icon @click="configDialog = false" color="indigo" class="mr-2">mdi-close</v-icon>
            <div>
              <div class="portal-config-dialog__title-text">Portal configurations</div>
            </div>
          </div>
          <v-spacer />

        </v-card-title>

        <v-divider />

        <v-card-text class="portal-config-dialog__body">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="portal-config-tile">
                <div class="portal-config-tile__meta">
                  <div class="portal-config-tile__label">Ask customer name</div>
                  <div class="portal-config-tile__hint">Collect the passenger name at the kiosk</div>
                </div>
                <v-switch
                  v-model="activePortal.config.name"
                  inset
                  dense
                  hide-details
                  color="indigo"
                />
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="portal-config-tile">
                <div class="portal-config-tile__meta">
                  <div class="portal-config-tile__label">Ask destination</div>
                  <div class="portal-config-tile__hint">Prompt for a destination address</div>
                </div>
                <v-switch
                  v-model="activePortal.config.destination"
                  inset
                  dense
                  hide-details
                  color="indigo"
                />
              </div>
            </v-col>
          </v-row>

          <div class="portal-config-section mt-4">
            <div class="portal-config-section__title">Accept name as</div>
            <v-radio-group
              v-model="activePortal.config.request_as"
              row
              class="mt-2"
              hide-details
            >
              <v-radio color="indigo" label="Notes" value="notes" />
              <v-radio color="indigo" label="Name" value="name" />
            </v-radio-group>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="portal-config-dialog__actions">
          <v-btn text @click="configDialog = false">Cancel</v-btn>
          <v-btn color="indigo" outlined @click="callUpdateConfig()">Update</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="networkDialog"
      max-width="620"
    >
      <v-card class="portal-network-dialog">
        <v-card-title class="portal-network-dialog__title">
          <div class="d-flex align-center">
            <v-icon @click="networkDialog = false" color="indigo" class="mr-2">mdi-close</v-icon>
            <div>
              <div class="portal-network-dialog__title-text">Portal network details</div>
              <div class="portal-network-dialog__subtitle">Latest connectivity and device metadata</div>
            </div>
          </div>
          <v-spacer />
          <!-- <v-btn icon aria-label="Close" class="portal-network-dialog__close" @click="networkDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn> -->
        </v-card-title>

        <v-divider />

        <v-card-text class="portal-network-dialog__body">
          <v-alert
            v-if="networkError"
            type="error"
            dense
            text
            border="left"
            class="mb-3 portal-network-error"
          >
            {{ networkError }}
          </v-alert>

          <div v-if="!activePortal || !activePortal.network_details" class="portal-network-empty">
            <v-icon color="grey lighten-1" size="40" class="mb-2">mdi-wifi-off</v-icon>
            <div class="portal-network-empty__title">No network details</div>
            <div class="portal-network-empty__hint">Click “Fetch latest” to load current details.</div>
          </div>

          <v-simple-table v-else class="portal-network-table">
            <thead>
              <tr>
                <th class="text-left">Key</th>
                <th class="text-left">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, index) in activePortal.network_details" :key="String(index)">
                <td class="portal-network-table__key">
                  {{ index }}
                </td>
                <td class="portal-network-table__val">
                  {{ p }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider />

        <v-card-actions class="portal-network-dialog__actions">
          <v-btn
            text
            @click="
              networkDialog = false;
              currentDevice = null;
            "
          >
            Cancel
          </v-btn>
          <v-btn
            color="indigo"
            outlined
            class="portal-network-fetch"
            :loading="resetLoader"
            :disabled="resetLoader"
            @click="fetchLatestNetworkDetails()"
          >
            <v-icon left small>mdi-cached</v-icon>
            Fetch latest
            <template v-slot:loader>
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="indigo"
              />
            </template>
          </v-btn>

          <!-- <v-btn color="success" text @click="fetchLatestNetworkDetails()">
            fetch latest
          </v-btn>-->
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove portal confirmation -->
    <v-dialog
      v-model="removeDialog"
      :max-width="$vuetify.breakpoint.xs ? '95%' : '440'"
      persistent
      overlay-opacity="0.7"
    >
      <v-card class="elevation-12 portal-delete-dialog">
        <v-card-title class="error white--text portal-delete-dialog__title">
          <span>Confirm deletion</span>
        </v-card-title>

        <v-card-text class="portal-delete-dialog__body">
          <div class="portal-delete-dialog__question">
            Are you sure you want to delete this portal?
          </div>

          <v-alert
            v-if="removeError"
            type="error"
            dense
            text
            border="left"
            class="mb-3"
          >
            {{ removeError }}
          </v-alert>

          <v-alert
            type="warning"
            outlined
            border="left"
            class="mb-0"
          >
            <div class="font-weight-medium">Warning:</div>
            <div class="text-caption mt-1">
              This action cannot be undone.
              <span v-if="removeTarget">
                (ID: {{ removeTarget.id }}, User: {{ removeTarget.username || '—' }}, Device: {{ removeTarget.device_id || '—' }})
              </span>
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions class="portal-delete-dialog__actions">
          <v-spacer />
          <v-btn
            text
            class="mr-3"
            :disabled="removeLoader"
            @click="removeDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            depressed
            class="px-6"
            :loading="removeLoader"
            :disabled="removeLoader"
            @click="confirmRemove()"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
  import moment from 'moment'
  import Vue from 'vue'
  import { status } from '@/services/User'
  import {
    search,
    updateConfig,
    restartPortal,
    removePortal,
    networkDetails,
    getPortal,
  } from '@/services/portals.js'
  import filterComponent from './filter'
  // Vue.filter('moment-ago', function (date) {
  //   return moment(date).fromNow()
  // });
  // Vue.filter('status', function (item) {
  //   if(item.status == 'online') {
  //     var val = moment(item.portal_last_active).add('minutes',30)
  //     return moment.duration(moment().diff(val)).asHours()>2 ? 'recently online': 'online';
  //   }
  //   else {
  //     return 'offline';
  //   }
  //   // return moment.duration(val).asMinutes()>30 ? 'offline': 'recently online'
  // });
  export default {
    name: 'PortalsList',
    components: {
      filterComponent,
      PagesBtn: () => import('./.././../../../pages/components/Btn'),
    },

    data () {
      return {
        loading: true,
        portals: [],
        expanded: [],
        headers: [
          { text: 'Details', value: 'data-table-expand' },
          { text: 'ID', align: 'center', sortable: false, value: 'id' },
          {
            text: 'Username',
            align: 'center',
            sortable: false,
            value: 'username',
          },
          {
            text: 'Password',
            align: 'center',
            sortable: false,
            value: 'password',
          },
          {
            text: 'Device',
            align: 'center',
            sortable: false,
            value: 'device_id',
          },
          {
            text: 'Portal Type',
            align: 'center',
            value: 'type',
            sortable: false,
          },
          { text: 'Role', align: 'center', value: 'role', sortable: false },
          { text: 'Status', align: 'center', value: 'status', sortable: false },
          {
            text: 'Bookings Count',
            align: 'center',
            value: 'booking_count.total',
            sortable: false,
          },
          {
            text: 'Last Active at',
            align: 'center',
            value: 'portal_last_active',
            sortable: false,
          },
          { text: 'Actions', align: 'center', value: 'actions', sortable: false, width: 190 },
        ],
        pagination: {
          current: 1,
          total: 0,
          totalPages: 0,
        },
        configDialog: false,
        activePortal: {
          config: {
            name: false,
            destination: false,
            request_as: 'name',
          },
          network_details: null,
          updated_at: null,
        },
        filterParams: {},
        networkDialog: false,
        currentDevice: null,
        resetLoader: false,
        networkError: '',

        removeDialog: false,
        removeTarget: null,
        removeLoader: false,
        removeError: '',
      }
    },
    mounted () {
      const params = {
        device_id: '',
        venues: [],
        portal_type: '',
        portal_role: '',
        page: 1,
      }
      this.handleSearch(params)
    },

    methods: {
      isExpanded (item) {
        return this.expanded.some(exp => exp.id === item.id)
      },
      toggleExpand (item) {
        const index = this.expanded.findIndex(exp => exp.id === item.id)
        if (index > -1) {
          this.expanded.splice(index, 1)
        } else {
          // Since single-expand is true, clear existing and add new
          this.expanded = [item]
        }
      },
      setModel (idx, model) {
        this.$set(
          this.portals,
          idx,
          model
        )
      },
      portalJoined (user) {
        const idx = this.portals.findIndex(p => p.user_id === user.id)
        let model = this.portals.find(p => p.user_id === user.id)
        if (model) {
          model.status = 'online'
          this.setModel(idx, model)
        }
      },
      portalLeft (user) {
        const idx = this.portals.findIndex(p => p.user_id === user.id)
        let model = this.portals.find(p => p.user_id === user.id)
        if (model) {
          model.status = 'offline'
          this.setModel(idx, model)
        }
      },
      listen () {
        window.Echo.join('taxipoint')
          .here((users) => {
            console.log('here')
            const onlineUsers = users.filter((u) => u.id !== this.$store.state.user.id && u.status === 'offline')
            const onlineIds = onlineUsers.map((o) => {
              let model = this.portals.find(p => p.user_id === o.id)
              let idx = this.portals.findIndex(p => p.user_id === o.id)
              if (model) {
                model.status = 'online'
                this.setModel(idx, model)
              }
            })
          })
          .joining((user) => {
            console.log('join')
            status(user.id, { status: 'online' }) // calling api
            this.portalJoined(user)
          })
          .leaving((user) => {
            console.log('leave')
            status(user.id, { status: 'offline' })
            this.portalLeft(user)
          })
      },
      handleSearch (params) {
        this.filterParams = params
        this.loading = true
        var queryString = Object.keys(params)
          .map((key) => key + '=' + params[key])
          .join('&')
        search(queryString).then((response) => {
          this.loading = false
          this.portals = response.data.obj.data.map(this.getDisplayPortal)
          this.pagination.total = response.data.obj.total
          this.pagination.totalPages = response.data.obj.last_page
          this.listen()
        })
      },

      handlePageChange (value) {
        this.pagination.current = value
        this.filterParams.page = value
        this.handleSearch(this.filterParams)
      },

      getDisplayPortal (portal) {
        return {
          id: portal.id,
          device_id: portal.device_id ? portal.device_id : '---',
          venue_name: portal.venue.name,
          venue_country: portal.venue.country,
          venue_city: portal.venue.city,
          venue_wallet_amount: portal.venue.wallet_amount,
          venue_address: portal.venue.street,
          venue_telephone: portal.venue.telephone,
          admin_email: portal.venue.admin.email,
          admin_api_host: portal.venue.admin.api_host,
          portal_last_active: portal.user.last_active,
          admin_api_password: portal.venue.admin.api_password,
          type: portal.type,
          role: portal.role ? portal.role : '---',
          booking_count: portal.booking_count,
          status: portal.user.status,
          config: portal.config,
          updated_at: portal.updated_at,
          network_details: portal.network_details,
          username: portal.user.email,
          password: portal.device_id ? '---' : portal.user.api_password,
          user_id: portal.user_id,
        }
      },

      portalConfig (val) {
        this.configDialog = true
        this.activePortal = val
      },
      callUpdateConfig () {
        updateConfig(this.activePortal).then((res) => {
          console.log('update config called')
          this.configDialog = false
        })
      },

      callRestart (val) {
        restartPortal(val.id)
      },

      callRemove (val) {
        this.removeTarget = val
        this.removeError = ''
        this.removeDialog = true
      },

      confirmRemove () {
        if (!this.removeTarget) return
        this.removeLoader = true
        this.removeError = ''
        removePortal(this.removeTarget.id)
          .then(() => {
            this.localRemoveObj(this.removeTarget)
            this.removeDialog = false
            this.removeTarget = null
          })
          .catch((err) => {
            const message =
              (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
              (err && err.message) ||
              'Failed to delete portal. Please try again.'
            this.removeError = message
          })
          .finally(() => {
            this.removeLoader = false
          })
      },

      localRemoveObj (val) {
        const portals = this.portals
        const idx = portals.findIndex((p) => p.id === val.id)
        portals.splice(idx, 1)
        this.portals = portals
      },

      getDeviceInfo (val) {
        this.networkDialog = true
        this.activePortal = val
        this.currentDevice = val.device_id
        this.networkError = ''
      },

      fetchLatestNetworkDetails () {
        this.resetLoader = true
        this.networkError = ''
        networkDetails(this.currentDevice).then((res) => {
          setTimeout(() => {
            getPortal(this.currentDevice)
              .then((data) => {
                this.resetLoader = false
                const oldTime = new Date(this.activePortal.updated_at)
                const newTime = new Date(data.data.obj.updated_at)
                if (oldTime >= newTime) {
                  status(data.data.obj.user_id, { status: 'offline' })
                  this.activePortal.status == 'online' ? this.activePortal.status == 'offline' : ''
                  this.networkError = 'Device is not reachable. Please try again in a moment.'
                } else {
                  this.activePortal.network_details =
                    data.data.obj.network_details
                  this.activePortal.updated_at = data.data.obj.updated_at
                }
              })
              .then(() => (this.resetLoader = false))
          }, 5000)
        })
      },
    },
  }
</script>

<style scoped>
  .modern-table >>> thead th {
    font-weight: 600 !important;
    font-size: 11px !important;
    letter-spacing: 0.3px;
    color: #424242 !important;
    text-transform: uppercase;
    background-color: #f5f5f5;
    border-bottom: 2px solid #e0e0e0 !important;
    padding: 8px 12px !important;
    height: 40px !important;
    white-space: nowrap !important;
  }

  .modern-table >>> tbody td {
    font-size: 13px;
    padding: 6px 12px !important;
    height: auto !important;
    vertical-align: middle;
  }

  .modern-table >>> tbody tr {
    height: auto !important;
    min-height: 40px;
    border-bottom: 1px solid #f0f0f0;
  }

  @media (max-width: 600px) {
    .modern-table >>> tbody td {
      font-size: 11px;
      padding: 5px 6px !important;
    }

    .modern-table >>> thead th {
      font-size: 10px !important;
      padding: 6px 4px !important;
      height: 36px !important;
    }

    .modern-table >>> tbody tr {
      min-height: 36px;
    }
  }

  /* Expand column: keep it visible and compact */
  .modern-table >>> th[data-column="data-table-expand"],
  .modern-table >>> td[data-column="data-table-expand"] {
    width: 44px !important;
    padding: 0 4px !important;
  }

  .expand-btn {
    width: 32px;
    height: 32px;
  }

  /* Keep all action icons visible on desktop (no wrapping/clipping) */
  .modern-table >>> th[data-column="actions"],
  .modern-table >>> td[data-column="actions"] {
    white-space: nowrap !important;
    min-width: 190px;
  }

  .modern-table >>> td[data-column="actions"] .v-btn {
    padding-left: 6px !important;
    padding-right: 6px !important;
    min-width: 0 !important;
  }

  /* Config dialog */
  .portal-config-dialog {
    border-radius: 14px;
    overflow: hidden;
  }

  /* Delete dialog */
  .portal-delete-dialog {
    border-radius: 14px;
    overflow: hidden;
  }

  .portal-delete-dialog__title {
    padding: 12px 16px;
    font-weight: 800;
    font-size: 14px;
    letter-spacing: 0.2px;
  }

  .portal-delete-dialog__body {
    padding: 14px 16px 8px !important;
  }

  .portal-delete-dialog__question {
    font-size: 13px;
    font-weight: 600;
    color: rgba(15, 23, 42, 0.78);
    margin-bottom: 10px;
    line-height: 1.35;
  }

  .portal-delete-dialog__actions {
    padding: 10px 12px 12px !important;
  }

  /* Make alerts in the delete dialog less tall */
  .portal-delete-dialog >>> .v-alert {
    border-radius: 12px;
    padding: 8px 10px !important;
  }

  .portal-delete-dialog >>> .v-alert__content {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.25 !important;
  }

  /* Expanded details panel */
  .portal-expanded {
    margin: 10px 0 14px;
    padding: 12px 12px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(63, 81, 181, 0.04) 0%, rgba(255, 255, 255, 1) 45%);
    border: 1px solid rgba(15, 23, 42, 0.08);
  }

  .portal-expanded__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px dashed rgba(15, 23, 42, 0.12);
    margin-bottom: 12px;
  }

  .portal-expanded__title {
    font-weight: 900;
    font-size: 13px;
    color: rgba(15, 23, 42, 0.78);
    letter-spacing: 0.2px;
    display: flex;
    align-items: center;
  }

  .portal-expanded__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  .portal-expanded__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  @media (max-width: 960px) {
    .portal-expanded__grid {
      grid-template-columns: 1fr;
    }
  }

  .portal-expanded__section {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    padding: 10px 10px;
  }

  .portal-expanded__section-title {
    font-weight: 900;
    font-size: 12px;
    color: rgba(15, 23, 42, 0.72);
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    letter-spacing: 0.2px;
  }

  .portal-expanded__kv {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 10px;
    padding: 6px 0;
    border-top: 1px solid rgba(15, 23, 42, 0.06);
  }

  .portal-expanded__kv:first-of-type {
    border-top: 0;
  }

  .portal-expanded__k {
    font-size: 11px;
    font-weight: 900;
    color: rgba(15, 23, 42, 0.55);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    line-height: 1.2;
  }

  .portal-expanded__v {
    font-size: 12.5px;
    color: rgba(15, 23, 42, 0.82);
    word-break: break-word;
    line-height: 1.25;
  }

  .portal-expanded__mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  }

  .portal-config-dialog__title {
    position: relative;
    align-items: flex-start;
    padding: 14px 52px 14px 16px;
    background: linear-gradient(180deg, rgba(63, 81, 181, 0.06) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .portal-config-dialog__close {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  .portal-config-dialog__title-text {
    font-weight: 800;
    color: rgba(15, 23, 42, 0.86);
    line-height: 1.2;
    justify-content: center;
    align-items: center;
    display: flex;
    flex-direction: column;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
  }

  .portal-config-dialog__subtitle {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.60);
    margin-top: 2px;
  }

  .portal-config-dialog__body {
    padding: 16px;
  }

  .portal-config-dialog__actions {
    justify-content: flex-end;
    padding: 10px 14px;
  }

  .portal-config-tile {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 12px;
    border: 1px solid rgba(15, 23, 42, 0.10);
    border-radius: 12px;
    background: #fff;
  }

  .portal-config-tile__label {
    font-weight: 800;
    color: rgba(15, 23, 42, 0.82);
    font-size: 13px;
  }

  .portal-config-tile__hint {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.60);
    margin-top: 2px;
  }

  .portal-config-section__title {
    font-weight: 800;
    color: rgba(15, 23, 42, 0.78);
    font-size: 12px;
    letter-spacing: 0.2px;
  }

  /* Network details dialog */
  .portal-network-dialog {
    border-radius: 14px;
    overflow: hidden;
  }

  .portal-network-dialog__title {
    position: relative;
    align-items: flex-start;
    padding: 14px 52px 14px 16px;
    background: linear-gradient(180deg, rgba(63, 81, 181, 0.06) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .portal-network-dialog__close {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  .portal-network-dialog__title-text {
    font-weight: 800;
    color: rgba(15, 23, 42, 0.86);
    line-height: 1.2;
  }

  .portal-network-dialog__subtitle {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.60);
    margin-top: 2px;
  }

  .portal-network-dialog__body {
    padding: 16px;
  }

  .portal-network-dialog__actions {
    justify-content: flex-end;
    padding: 10px 14px;
  }

  .portal-network-fetch {
    border-radius: 10px;
    text-transform: none;
    font-weight: 800;
    letter-spacing: 0;
  }

  .portal-network-item {
    border: 1px solid rgba(15, 23, 42, 0.10);
    border-radius: 12px;
    background: #fff;
    padding: 10px 12px;
  }

  .portal-network-item__key {
    font-size: 11px;
    font-weight: 800;
    color: rgba(15, 23, 42, 0.58);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .portal-network-item__val {
    font-size: 12.5px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    color: rgba(15, 23, 42, 0.82);
    word-break: break-word;
  }

  .portal-network-table {
    border: 1px solid rgba(15, 23, 42, 0.10);
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
  }

  .portal-network-table >>> thead th {
    font-size: 11px;
    font-weight: 800;
    color: rgba(15, 23, 42, 0.60);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    background: rgba(63, 81, 181, 0.04);
    padding: 10px 12px;
    white-space: nowrap;
  }

  .portal-network-table >>> tbody td {
    padding: 10px 12px;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    vertical-align: top;
    font-size: 12.5px;
  }

  .portal-network-table__key {
    width: 38%;
    font-weight: 800;
    color: rgba(15, 23, 42, 0.65);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .portal-network-table__val {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    color: rgba(15, 23, 42, 0.82);
    word-break: break-word;
  }

  .portal-network-empty {
    padding: 18px 12px;
    text-align: center;
  }

  .portal-network-empty__title {
    font-weight: 800;
    color: rgba(15, 23, 42, 0.76);
  }

  .portal-network-empty__hint {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.55);
    margin-top: 4px;
  }

  .portal-network-error {
    border-radius: 12px;
    padding: 6px 10px !important;
    font-size: 12px !important;
    line-height: 1.25 !important;
  }

  .portal-network-error >>> .v-alert__content {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Compact row styling */
  .modern-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }

  .modern-table >>> .v-data-table {
    border-collapse: separate;
    border-spacing: 0;
  }

  .table-row {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .table-row:hover {
    background: linear-gradient(90deg, rgba(63, 81, 181, 0.05) 0%, rgba(63, 81, 181, 0.02) 100%) !important;
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  /* Smooth scrollbar */
  .modern-table >>> .v-data-table__wrapper {
    scrollbar-width: thin;
    scrollbar-color: #bdbdbd #f5f5f5;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 4px;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 4px;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
    background: #9e9e9e;
  }

  /* Expanded row styling */
  .modern-table >>> .v-data-table__expanded__content {
    background-color: #fafafa !important;
    border-top: 2px solid #e0e0e0;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .action-btn:hover {
    transform: scale(1.15) rotate(5deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
</style>
